import React, { useMemo, useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import EmiCalculator from './components/EmiCalculator';
import PrepaymentPanel from './components/PrepaymentPanel';
import AmortizationTable from './components/AmortizationTable';
import AdvancedSection from './components/AdvancedSection';
import AdSlot from './components/AdSlot';
import Footer from './components/Footer';
import { AppSettingsProvider, useAppSettings } from './lib/i18n';
import { defaultValues } from './mock';
import { exportEmiPdf } from './lib/pdfExport';
import useLoanSimulation from './hooks/useLoanSimulation';
import { exportAmortizationCsv } from './lib/csvExport';
import AboutPage from './pages/AboutPage';
import FaqPage from './pages/FaqPage';
import PrivacyPage from './pages/PrivacyPage';
import ContactPage from './pages/ContactPage';
import { decodeParamsToState } from './lib/share';

function Home() {
  const { currency, t } = useAppSettings();
  const location = useLocation();
  const initial = useMemo(() => decodeParamsToState(location.search) || {}, [location.search]);

  const [loanAmount, setLoanAmount] = useState(initial.loanAmount ?? defaultValues.loanAmount);
  const [interestRate, setInterestRate] = useState(initial.interestRate ?? defaultValues.interestRate);
  const [tenure, setTenure] = useState(initial.tenure ?? defaultValues.loanTenure);
  const [tenureType, setTenureType] = useState(initial.tenureType ?? defaultValues.tenureType);
  const [moratoriumEnabled, setMoratoriumEnabled] = useState(initial.moratoriumEnabled ?? defaultValues.moratoriumEnabled);
  const [moratoriumMonths, setMoratoriumMonths] = useState(initial.moratoriumMonths ?? defaultValues.moratoriumMonths);
  const [moratoriumType, setMoratoriumType] = useState(initial.moratoriumType ?? defaultValues.moratoriumType);
  const [interestMethod, setInterestMethod] = useState(initial.interestMethod ?? defaultValues.interestMethod);
  const [chartType, setChartType] = useState(initial.chartType ?? defaultValues.chartType);
  const [prepayments, setPrepayments] = useState(initial.prepayments ?? []);
  const [extraEmi, setExtraEmi] = useState(initial.extraEmi ?? 0);
  const [advanced, setAdvanced] = useState({ ...defaultValues.advanced, ...(initial.advanced || {}) });

  const tenureMonths = tenureType === 'Yr' ? tenure * 12 : tenure;
  const moratorium = useMemo(
    () => ({ enabled: moratoriumEnabled, months: moratoriumMonths, type: moratoriumType }),
    [moratoriumEnabled, moratoriumMonths, moratoriumType]
  );

  const { current: simulation, baseline, plain } = useLoanSimulation({
    loanAmount, interestRate, tenureMonths, interestMethod,
    moratorium, prepayments, extraEmi, advanced,
  });

  const handleDownloadPdf = () => {
    exportEmiPdf({
      loanAmount, interestRate, tenureMonths, moratorium,
      extraEmi, prepayments,
      summary: simulation.summary,
      schedule: simulation.schedule,
      plainSummary: plain.summary,
      currency, interestMethod, advanced, chartType,
      t,
    });
  };

  const handleDownloadCsv = () => {
    exportAmortizationCsv({
      schedule: simulation.schedule,
      loanAmount,
      interestRate,
      tenureMonths,
      currency,
      advanced,
      summary: simulation.summary,
      plainSummary: plain.summary,
      moratorium,
      extraEmi,
      prepayments,
      interestMethod,
    });
  };

  const buildShareState = () => ({
    loanAmount, interestRate, tenure, tenureType,
    moratoriumEnabled, moratoriumMonths, moratoriumType,
    interestMethod, chartType, extraEmi, prepayments, advanced,
  });

  return (
    <div className="min-h-screen bg-[#F7FAFC] dark:bg-[#0B1220] flex flex-col relative transition-colors">
      <div className="ad-rail ad-rail-left" aria-hidden="true">
        <div className="ad-slot"><AdSlot side="left" /></div>
      </div>
      <div className="ad-rail ad-rail-right" aria-hidden="true">
        <div className="ad-slot"><AdSlot side="right" /></div>
      </div>

      <Header />

      <EmiCalculator
        loanAmount={loanAmount} setLoanAmount={setLoanAmount}
        interestRate={interestRate} setInterestRate={setInterestRate}
        tenure={tenure} setTenure={setTenure}
        tenureType={tenureType} setTenureType={setTenureType}
        moratoriumEnabled={moratoriumEnabled} setMoratoriumEnabled={setMoratoriumEnabled}
        moratoriumMonths={moratoriumMonths} setMoratoriumMonths={setMoratoriumMonths}
        moratoriumType={moratoriumType} setMoratoriumType={setMoratoriumType}
        interestMethod={interestMethod} setInterestMethod={setInterestMethod}
        chartType={chartType} setChartType={setChartType}
        simulation={simulation}
        buildShareState={buildShareState}
      />

      <AmortizationTable
        schedule={simulation.schedule}
        monthsTaken={simulation.summary.monthsTaken}
        onDownloadPdf={handleDownloadPdf}
        onDownloadCsv={handleDownloadCsv}
      />

      <AdvancedSection
        advanced={advanced}
        setAdvanced={setAdvanced}
        currentSimulation={simulation}
        plainSimulation={plain}
        loanAmount={loanAmount}
        interestRate={interestRate}
        chartType={chartType}
        moratoriumEnabled={moratoriumEnabled}
        moratoriumType={moratoriumType}
      />

      <PrepaymentPanel
        prepayments={prepayments}
        setPrepayments={setPrepayments}
        extraEmi={extraEmi}
        setExtraEmi={setExtraEmi}
        baseline={baseline}
        current={simulation.summary}
        currentFull={simulation}
        chartType={chartType}
        loanAmount={loanAmount}
        moratoriumEnabled={moratoriumEnabled}
        moratoriumType={moratoriumType}
        maxMonth={simulation.summary.monthsTaken || tenureMonths}
      />

      <Footer />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <AppSettingsProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
        </BrowserRouter>
      </AppSettingsProvider>
    </div>
  );
}

export default App;
